// /Users/pramod/Desktop/school-erp/frontend/src/Parent/components/AttendanceView.js

import React, { useState } from 'react';

const AttendanceView = ({ data }) => {
  const [viewMode, setViewMode] = useState('subject');
  const [selectedSubject, setSelectedSubject] = useState('all');

  if (!data) {
    return (
      <div className="bg-slate-50 border border-slate-100 rounded-2xl p-8 text-center shadow-sm">
        <div className="text-4xl mb-2">📊</div>
        <p className="text-slate-400 text-sm font-medium">No attendance details are available right now.</p>
      </div>
    );
  }

  const { summary, subjectWise, sessions = [] } = data;
  const { total, present, absent, late, medical, percentage } = summary || {};
  const subjectOptions = subjectWise || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'present':
      case 'on_duty':
        return 'bg-emerald-50 text-emerald-700 border-emerald-100';
      case 'absent':
        return 'bg-rose-50 text-rose-700 border-rose-100';
      case 'late':
        return 'bg-amber-50 text-amber-700 border-amber-100';
      case 'medical':
        return 'bg-blue-50 text-blue-700 border-blue-100';
      case 'excused':
        return 'bg-purple-50 text-purple-700 border-purple-100';
      default:
        return 'bg-slate-50 text-slate-600 border-slate-200';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'present':
      case 'on_duty':
        return 'bg-emerald-500';
      case 'absent':
        return 'bg-rose-500';
      case 'late':
        return 'bg-amber-500';
      case 'medical':
        return 'bg-blue-500';
      case 'excused':
        return 'bg-purple-500';
      default:
        return 'bg-slate-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present':
      case 'on_duty':
        return '✅';
      case 'absent':
        return '❌';
      case 'late':
        return '⏰';
      case 'medical':
        return '🏥';
      case 'excused':
        return '📋';
      default:
        return '📌';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'present':
        return 'Present';
      case 'on_duty':
        return 'School Duty';
      case 'absent':
        return 'Absent';
      case 'late':
        return 'Late';
      case 'medical':
        return 'Sick Leave';
      case 'excused':
        return 'Excused';
      default:
        return status || 'Marked';
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDayOfWeek = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { weekday: 'long' });
  };

  const filteredSessions = selectedSubject === 'all' 
    ? sessions 
    : sessions.filter(s => s.subjectId === selectedSubject);

  const groupedSessions = {};
  filteredSessions.forEach(session => {
    const dateKey = session.date ? new Date(session.date).toISOString().split('T')[0] : 'unknown';
    if (!groupedSessions[dateKey]) {
      groupedSessions[dateKey] = [];
    }
    groupedSessions[dateKey].push(session);
  });

  const sortedDates = Object.keys(groupedSessions).sort((a, b) => new Date(b) - new Date(a));

  const getBarColor = (pct) => {
    if (pct >= 85) return 'bg-emerald-500';
    if (pct >= 75) return 'bg-amber-500';
    if (pct >= 60) return 'bg-orange-500';
    return 'bg-rose-500';
  };

  return (
    <div className="space-y-6">
      
      {/* ================= HEADER SECTION: OVERALL SUMMARY CARD ================= */}
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-0.5">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Attendance Score</p>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-black text-slate-900 tracking-tight">{percentage || 0}%</span>
              <span className="text-xs text-slate-400 font-semibold">
                (Attended {present || 0} out of {total || 0} classes)
              </span>
            </div>
          </div>

          {/* Stat Mini Blocks */}
          <div className="grid grid-cols-4 gap-2 w-full md:w-auto">
            <div className="bg-emerald-50/60 border border-emerald-100 rounded-xl p-2.5 text-center min-w-[70px]">
              <p className="text-base font-black text-emerald-700">{present || 0}</p>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mt-0.5">Present</p>
            </div>
            <div className="bg-rose-50/60 border border-rose-100 rounded-xl p-2.5 text-center min-w-[70px]">
              <p className="text-base font-black text-rose-700">{absent || 0}</p>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mt-0.5">Absent</p>
            </div>
            <div className="bg-amber-50/60 border border-amber-100 rounded-xl p-2.5 text-center min-w-[70px]">
              <p className="text-base font-black text-amber-700">{late || 0}</p>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mt-0.5">Late</p>
            </div>
            <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-2.5 text-center min-w-[70px]">
              <p className="text-base font-black text-blue-700">{medical || 0}</p>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mt-0.5">Sick</p>
            </div>
          </div>
        </div>

        {/* Outer Visual Slider Progress Track */}
        <div className="space-y-1">
          <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden shadow-inner">
            <div
              className={`h-full rounded-full transition-all duration-500 shadow-sm ${getBarColor(percentage)}`}
              style={{ width: `${percentage || 0}%` }}
            />
          </div>
          <div className="flex justify-between items-center text-[10px] text-slate-400 font-bold uppercase tracking-wider px-0.5">
            <span>Minimum Target: 75%</span>
            <span>Status: {percentage >= 75 ? '👍 Safe Track' : '⚠️ Below Target'}</span>
          </div>
        </div>
      </div>

      {/* ================= CONTROLS ROW: TOGGLES AND FILTERS ================= */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50/50 border border-slate-100 p-2 rounded-2xl">
        <div className="flex gap-1 bg-slate-200/60 p-1 rounded-xl w-full sm:w-auto">
          <button
            onClick={() => setViewMode('subject')}
            className={`flex-1 sm:flex-initial px-4 py-2 text-xs font-bold rounded-lg transition-all active:scale-95 ${
              viewMode === 'subject' 
                ? 'bg-white text-slate-950 shadow-sm border border-slate-200/30' 
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            Sort by Subject
          </button>
          <button
            onClick={() => setViewMode('daily')}
            className={`flex-1 sm:flex-initial px-4 py-2 text-xs font-bold rounded-lg transition-all active:scale-95 ${
              viewMode === 'daily' 
                ? 'bg-white text-slate-950 shadow-sm border border-slate-200/30' 
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            Sort by Date / Day
          </button>
        </div>

        {/* Context Subject Dropdown Filter Element */}
        {viewMode === 'daily' && subjectOptions.length > 1 && (
          <div className="relative w-full sm:w-auto">
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="w-full sm:w-auto pl-3 pr-8 py-2 text-xs font-semibold bg-white border border-slate-200 rounded-xl appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 cursor-pointer text-slate-700 shadow-sm"
            >
              <option value="all">📚 Show All Subjects</option>
              {subjectOptions.map((sub) => (
                <option key={sub.subjectId} value={sub.subjectId}>
                  📘 {sub.subjectName}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7"/></svg>
            </div>
          </div>
        )}
      </div>

      {/* ================= OPTION A VIEW: SUBJECT-WISE CARDS ================= */}
      {viewMode === 'subject' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {subjectWise && subjectWise.length > 0 ? (
            subjectWise.map((subject, index) => {
              const attendancePercentage = subject.total > 0 
                ? Math.round((subject.present / subject.total) * 100) 
                : 0;
              const hasGoodAttendance = attendancePercentage >= 75;
              
              return (
                <div 
                  key={index} 
                  className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 transition-all flex flex-col justify-between space-y-4"
                >
                  <div className="flex items-start gap-3 justify-between">
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 text-sm truncate">{subject.subjectName}</p>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <span className="text-xs text-slate-400 font-medium">Code: {subject.subjectCode || '—'}</span>
                        {subject.isLab && <span className="text-[9px] font-bold text-blue-600 bg-blue-50 border border-blue-100/60 px-1.5 py-0.5 rounded-md">Practical / Lab</span>}
                        {subject.isElective && <span className="text-[9px] font-bold text-purple-600 bg-purple-50 border border-purple-100/60 px-1.5 py-0.5 rounded-md">Chosen Subject</span>}
                        {subject.isLanguage && <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-100/60 px-1.5 py-0.5 rounded-md">Language</span>}
                      </div>
                    </div>

                    <div className="text-right flex-shrink-0">
                      <p className="text-base font-black text-slate-900 tracking-tight">{attendancePercentage}%</p>
                      <p className="text-[10px] text-slate-400 font-medium">{subject.present}/{subject.total} Days</p>
                    </div>
                  </div>

                  {/* Individual Bar Component Slider */}
                  <div className="space-y-3 pt-2 border-t border-slate-50">
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden shadow-inner">
                      <div
                        className={`h-full rounded-full transition-all duration-500 shadow-sm ${getBarColor(attendancePercentage)}`}
                        style={{ width: `${attendancePercentage}%` }}
                      />
                    </div>

                    {/* Horizontal Score Strip Counters */}
                    <div className="grid grid-cols-5 gap-1 text-center text-[10px] font-semibold">
                      <div className="bg-slate-50/50 p-1 rounded-md"><span className="block font-bold text-emerald-600">{subject.present || 0}</span><span className="text-slate-400 text-[8px]">Present</span></div>
                      <div className="bg-slate-50/50 p-1 rounded-md"><span className="block font-bold text-rose-600">{subject.absent || 0}</span><span className="text-slate-400 text-[8px]">Absent</span></div>
                      <div className="bg-slate-50/50 p-1 rounded-md"><span className="block font-bold text-amber-600">{subject.late || 0}</span><span className="text-slate-400 text-[8px]">Late</span></div>
                      <div className="bg-slate-50/50 p-1 rounded-md"><span className="block font-bold text-blue-600">{subject.medical || 0}</span><span className="text-slate-400 text-[8px]">Sick</span></div>
                      <div className="bg-slate-50/50 p-1 rounded-md"><span className="block font-bold text-purple-600">{subject.excused || 0}</span><span className="text-slate-400 text-[8px]">Leave</span></div>
                    </div>

                    <div className={`w-full py-1.5 text-[10px] font-bold rounded-xl border text-center uppercase tracking-wide ${
                      hasGoodAttendance ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-rose-50 text-rose-700 border-rose-100'
                    }`}>
                      {hasGoodAttendance ? '✅ Good Track' : '⚠️ Needs Attention'}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-span-full bg-slate-50 border border-slate-100 rounded-2xl p-8 text-center shadow-sm">
              <p className="text-slate-400 text-sm font-medium">No subject records available.</p>
            </div>
          )}
        </div>
      )}

      {/* ================= OPTION B VIEW: DAY-WISE TRACK TIMELINE ================= */}
      {viewMode === 'daily' && (
        <div className="space-y-4">
          {sortedDates.length > 0 ? (
            sortedDates.map((dateKey) => {
              const daySessions = groupedSessions[dateKey];
              const dayTotal = daySessions.length;
              const dayPresent = daySessions.filter(s => s.status === 'present' || s.status === 'on_duty').length;
              const dayPercentage = dayTotal > 0 ? Math.round((dayPresent / dayTotal) * 100) : 0;

              return (
                <div key={dateKey} className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-md transition-all">
                  
                  {/* Nested Row Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100 gap-2">
                    <div className="min-w-0">
                      <span className="text-sm font-bold text-slate-900 tracking-tight block sm:inline mr-2">
                        {formatDate(dateKey)}
                      </span>
                      <span className="text-xs text-slate-400 font-semibold uppercase tracking-wide">
                        {getDayOfWeek(dateKey)}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-3 self-end sm:self-auto">
                      <span className="text-xs font-semibold text-slate-500">
                        Attended {dayPresent}/{dayTotal} classes
                      </span>
                      <span className={`text-xs font-black tracking-tight ${dayPercentage >= 75 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {dayPercentage}%
                      </span>
                      <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden hidden sm:block">
                        <div
                          className={`h-full rounded-full ${getBarColor(dayPercentage)}`}
                          style={{ width: `${dayPercentage}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Hourly Period List Slots */}
                  <div className="divide-y divide-slate-50">
                    {daySessions.map((session, idx) => {
                      const subject = subjectWise.find(s => s.subjectId === session.subjectId);
                      return (
                        <div key={idx} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50/40 transition-colors gap-3">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusBg(session.status)}`} />
                            <div className="min-w-0">
                              <p className="text-xs font-bold text-slate-800 truncate">
                                {session.subjectName || subject?.subjectName || 'School Period'}
                              </p>
                              <p className="text-[11px] text-slate-400 font-medium truncate mt-0.5">
                                🕒 {session.startTime && session.endTime 
                                  ? `${session.startTime} - ${session.endTime}`
                                  : 'Regular Hours'}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {session.isLab && (
                              <span className="text-[9px] font-bold text-blue-600 bg-blue-50 border border-blue-100/60 px-1.5 py-0.5 rounded-md hidden sm:inline-block">Lab</span>
                            )}
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold border shadow-sm/50 ${getStatusColor(session.status)}`}>
                              <span>{getStatusIcon(session.status)}</span>
                              <span>{getStatusLabel(session.status)}</span>
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-8 text-center shadow-sm">
              <div className="text-4xl mb-2">📅</div>
              <p className="text-slate-400 text-sm font-medium">No schedule activity found for this filter selection.</p>
            </div>
          )}

          {sessions.length > 0 && (
            <p className="text-center text-[11px] font-semibold text-slate-400 pt-1">
              Showing total history log of {filteredSessions.length} active logs
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AttendanceView;